<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '50rem' }"
    @update:visible="() => $emit('update:visible', false)"
  >
    <template #header>
      <div class="flex w-full justify-between items-center">
        <h3 class="text-lg font-semibold">
          Available Liquid Variables
        </h3>
        <Button
          severity="secondary"
          label="Close"
          outlined
          @click="() => $emit('update:visible', false)"
        />
      </div>
    </template>
    <div v-if="hasAnyVariables">
      <InputText
        v-model="filterText"
        placeholder="Filter variables..."
        class="w-full mb-4"
      />
      <div class="border border-brand-border rounded-md overflow-hidden">
        <table class="w-full text-sm">
          <tbody class="divide-y divide-brand-border">
            <template
              v-for="section in filteredSections"
              :key="section.title"
            >
              <tr class="bg-brand-surface-secondary/30">
                <td
                  colspan="2"
                  class="px-4 py-2 font-semibold text-brand-text-secondary text-xs uppercase tracking-wide"
                >
                  {{ section.title }}
                </td>
              </tr>
              <tr
                v-for="variable in section.variables"
                :key="variable.liquid"
                class="hover:bg-brand-surface-secondary/50"
              >
                <td class="px-4 py-3 font-mono text-brand-text dark:text-white/70 break-all">
                  {{ variable.liquid }}
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  <Button
                    size="small"
                    outlined
                    label="Add"
                    :loading="section.loading"
                    @click="addVariable(variable.liquid)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-else
      class="text-center py-8 text-brand-text-secondary"
    >
      <p>No Liquid variables available in this flow.</p>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { useFlowLiquidVariables } from '@/modules/flow/composables/useFlowLiquidVariables'
import { useIntegrationDataStore } from '@/modules/integration/stores/integrationDataStore'
import { useGlobalLiquidVariables } from '@/composables/useGlobalLiquidVariables'

const emit = defineEmits(['add', 'update:visible'])
const props = defineProps({
  visible: Boolean
})

const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)
const integrationDataStore = useIntegrationDataStore()

const { availableLiquidVariables } = useFlowLiquidVariables()
const globalVariables = useGlobalLiquidVariables()

// Filter text for searching variables
const filterText = ref('')

// Fetch custom fields from integration
const customFieldsData = ref<any[]>([])
const isLoadingFields = ref(false)

watchEffect(async () => {
  if (integratedPartner.value && props.visible) {
    isLoadingFields.value = true
    try {
      const customFields = await integrationDataStore.getCustomFields()
      customFieldsData.value = customFields
    } catch (error) {
      console.error('Error fetching custom fields:', error)
      customFieldsData.value = []
    } finally {
      isLoadingFields.value = false
    }
  }
})

// Helper to filter variables based on search text
const filterVariables = (variables: any[]) => {
  if (!filterText.value.trim()) {
    return variables
  }

  const search = filterText.value.toLowerCase()
  return variables.filter(variable =>
    variable.liquid.toLowerCase().includes(search) ||
    variable.description?.toLowerCase().includes(search)
  )
}

// Build all sections dynamically
const allSections = computed(() => {
  const sections = []

  // Flow Variables Section
  if (availableLiquidVariables.value.length > 0) {
    sections.push({
      title: 'Flow Variables',
      variables: availableLiquidVariables.value,
      loading: false
    })
  }

  // Integration Native Fields Section
  if (integratedPartner.value) {
    const partnerName = integratedPartner.value.name
    sections.push({
      title: `${integratedPartner.value.label || 'ESP'} Fields`,
      variables: [
        {
          liquid: `{{ ${partnerName}.email }}`,
          description: 'Subscriber email address'
        },
        {
          liquid: `{{ ${partnerName}.subscriberId }}`,
          description: 'Subscriber ID in the email platform'
        }
      ],
      loading: false
    })
  }

  // Integration Custom Fields Section
  if (integratedPartner.value && customFieldsData.value.length > 0) {
    const partnerName = integratedPartner.value.name
    sections.push({
      title: `${integratedPartner.value.label || 'ESP'} Custom Fields`,
      variables: customFieldsData.value.map(field => ({
        liquid: `{{ ${partnerName}.customFields.${field.templatingToken || field.id} }}`,
        description: field.label || field.name || `Custom Field ${field.id}`
      })),
      loading: isLoadingFields.value
    })
  }

  // Global Variable Sections
  const globalSections = [
    { title: 'Current Page', variables: globalVariables.currentpage },
    { title: 'Referrer', variables: globalVariables.referrer },
    { title: 'UTM Parameters', variables: globalVariables.utm },
    { title: 'Location', variables: globalVariables.location },
    { title: 'Query Parameters', variables: globalVariables.query },
    { title: 'JavaScript Variables', variables: globalVariables.jsdata }
  ]

  globalSections.forEach(section => {
    if (section.variables.length > 0) {
      sections.push({
        ...section,
        loading: false
      })
    }
  })

  return sections
})

// Filter sections based on search text and remove empty sections
const filteredSections = computed(() => {
  return allSections.value
    .map(section => ({
      ...section,
      variables: filterVariables(section.variables)
    }))
    .filter(section => section.variables.length > 0)
})

// Check if there are any variables available
const hasAnyVariables = computed(() => allSections.value.length > 0)

const addVariable = (liquid: string) => {
  emit('add', liquid)
  emit('update:visible', false)
}
</script>
